import Header from "./Header";
import Footer from "./Footer";
import ChatBot from "./ChatBot";
import "../styles/CookiesPolicy.css";
import { useEffect, useState } from "react";
import API_BASE_URL from "../config";
import {
    FaCookieBite,
    FaShieldAlt,
    FaCog,
    FaChartLine,
    FaUserCheck,
    FaClock,
    FaEnvelope,
    FaCheckCircle,
    FaExclamationTriangle,
    FaEye,
    FaBan,
    FaGlobe,
    FaHistory,
    FaChevronRight,
    FaQuestionCircle,
    FaDownload,
    FaRegCopy,
    FaDesktop,
    FaMobileAlt,
    FaDatabase
} from "react-icons/fa";

export default function CookiesPolicy() {
    const [contactEmail, setContactEmail] = useState("");
    const [activeTab, setActiveTab] = useState("introduction");
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const fetchSiteSettings = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/site-settings`);
                const data = await res.json();
                if (data?.contactEmail) {
                    setContactEmail(data.contactEmail);
                }
            } catch (err) {
                console.error("Error fetching site settings:", err);
            }
        };

        const checkMobile = () => {
            setIsMobile(window.innerWidth < 1024);
        };

        fetchSiteSettings();
        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const tabs = [
        {
            id: "introduction",
            title: "Introduction",
            subtitle: "What cookies are & their purpose",
            icon: <FaCookieBite />,
            color: "#d6b87c"
        },
        {
            id: "categories",
            title: "Cookie Categories",
            subtitle: "Types & purposes of cookies we use",
            icon: <FaDatabase />,
            color: "#c2a57a"
        },
        {
            id: "management",
            title: "Cookie Management",
            subtitle: "How to control cookie preferences",
            icon: <FaCog />,
            color: "#b8945c"
        },
        {
            id: "third-party",
            title: "Third-Party Cookies",
            subtitle: "External services & their cookies",
            icon: <FaGlobe />,
            color: "#a57a42"
        },
        {
            id: "consent",
            title: "Consent & Legal",
            subtitle: "Your rights & our compliance",
            icon: <FaUserCheck />,
            color: "#d6b87c"
        },
        {
            id: "updates",
            title: "Updates & Contact",
            subtitle: "Policy changes & contact information",
            icon: <FaHistory />,
            color: "#c2a57a"
        }
    ];

    const renderContent = () => {
        switch (activeTab) {
            case "introduction":
                return (
                    <div className="jus-cookies-tab-content">
                        <div className="jus-cookies-content-header">
                            <div className="jus-cookies-content-icon" style={{ background: "linear-gradient(135deg, #d6b87c 0%, #c2a57a 100%)" }}>
                                <FaCookieBite />
                            </div>
                            <div>
                                <h2>Understanding Cookies</h2>
                                <p className="jus-cookies-content-subtitle">
                                    Cookies are small text files that help us provide a secure, reliable, and optimized experience on our legal matching platform.
                                </p>
                            </div>
                        </div>

                        <div className="jus-cookies-intro-grid">
                            <div className="jus-cookies-intro-card">
                                <div className="jus-cookies-intro-icon" style={{ backgroundColor: "rgba(214, 184, 124, 0.1)" }}>
                                    <FaDesktop />
                                </div>
                                <div className="jus-cookies-intro-content">
                                    <h3>Technical Definition</h3>
                                    <p>Cookies are small pieces of data stored on your device when you visit websites. They enable websites to remember your actions and preferences over time.</p>
                                    <ul>
                                        <li><strong>Text-based data</strong> stored in your browser</li>
                                        <li><strong>Session-based</strong> or persistent storage</li>
                                        <li><strong>Domain-specific</strong> - only accessible by the issuing website</li>
                                        <li><strong>Encrypted</strong> for security-sensitive information</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="jus-cookies-intro-card">
                                <div className="jus-cookies-intro-icon" style={{ backgroundColor: "rgba(194, 165, 122, 0.1)" }}>
                                    <FaShieldAlt />
                                </div>
                                <div className="jus-cookies-intro-content">
                                    <h3>Why We Use Cookies</h3>
                                    <p>Cookies are essential for providing a functional, secure, and personalized experience on our legal services platform.</p>
                                    <ul>
                                        <li><strong>Security:</strong> Prevent fraud and unauthorized access</li>
                                        <li><strong>Authentication:</strong> Keep you logged in securely</li>
                                        <li><strong>Functionality:</strong> Enable core platform features</li>
                                        <li><strong>Performance:</strong> Optimize loading and responsiveness</li>
                                        <li><strong>Analytics:</strong> Understand and improve user experience</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="jus-cookies-compliance-section">
                            <div className="jus-cookies-compliance-header">
                                <FaCheckCircle className="jus-cookies-compliance-icon" />
                                <h3>Legal Compliance</h3>
                            </div>
                            <p>
                                Our use of cookies complies with UK GDPR, Data Protection Act 2018, Privacy and Electronic Communications Regulations (PECR),
                                and ICO guidance on cookies and similar technologies.
                            </p>
                        </div>
                    </div>
                );

            case "categories":
                return (
                    <div className="jus-cookies-tab-content">
                        <div className="jus-cookies-content-header">
                            <div className="jus-cookies-content-icon" style={{ background: "linear-gradient(135deg, #c2a57a 0%, #b8945c 100%)" }}>
                                <FaDatabase />
                            </div>
                            <div>
                                <h2>Cookie Categories & Purposes</h2>
                                <p className="jus-cookies-content-subtitle">
                                    We use different types of cookies for specific, lawful purposes on our platform.
                                </p>
                            </div>
                        </div>

                        <div className="jus-cookies-category-grid">
                            <div className="jus-cookies-category-card">
                                <div className="jus-cookies-category-header">
                                    <div className="jus-cookies-category-icon" style={{ backgroundColor: "rgba(214, 184, 124, 0.1)" }}>
                                        <FaCheckCircle />
                                    </div>
                                    <h3>Strictly Necessary Cookies</h3>
                                </div>
                                <p>Essential for platform operation. These cookies cannot be disabled as they are required for basic functionality.</p>
                                <div className="jus-cookies-category-details">
                                    <div className="jus-cookies-category-detail">
                                        <span className="jus-cookies-detail-label">Purpose:</span>
                                        <span className="jus-cookies-detail-value">Platform security and core functionality</span>
                                    </div>
                                    <div className="jus-cookies-category-detail">
                                        <span className="jus-cookies-detail-label">Storage Duration:</span>
                                        <span className="jus-cookies-detail-value">Session or up to 24 hours</span>
                                    </div>
                                    <div className="jus-cookies-category-detail">
                                        <span className="jus-cookies-detail-label">Examples:</span>
                                        <div className="jus-cookies-examples">
                                            <span>Authentication tokens</span>
                                            <span>CSRF protection</span>
                                            <span>Load balancing</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="jus-cookies-category-card">
                                <div className="jus-cookies-category-header">
                                    <div className="jus-cookies-category-icon" style={{ backgroundColor: "rgba(194, 165, 122, 0.1)" }}>
                                        <FaChartLine />
                                    </div>
                                    <h3>Analytics & Performance Cookies</h3>
                                </div>
                                <p>Help us understand how users interact with our platform so we can improve services and user experience.</p>
                                <div className="jus-cookies-category-details">
                                    <div className="jus-cookies-category-detail">
                                        <span className="jus-cookies-detail-label">Purpose:</span>
                                        <span className="jus-cookies-detail-value">Platform optimization and improvement</span>
                                    </div>
                                    <div className="jus-cookies-category-detail">
                                        <span className="jus-cookies-detail-label">Storage Duration:</span>
                                        <span className="jus-cookies-detail-value">Up to 24 months</span>
                                    </div>
                                    <div className="jus-cookies-category-detail">
                                        <span className="jus-cookies-detail-label">Examples:</span>
                                        <div className="jus-cookies-examples">
                                            <span>Usage analytics</span>
                                            <span>Error tracking</span>
                                            <span>Performance monitoring</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="jus-cookies-category-card">
                                <div className="jus-cookies-category-header">
                                    <div className="jus-cookies-category-icon" style={{ backgroundColor: "rgba(184, 148, 92, 0.1)" }}>
                                        <FaCog />
                                    </div>
                                    <h3>Functional Cookies</h3>
                                </div>
                                <p>Remember your preferences and settings to enhance your experience and personalize the platform.</p>
                                <div className="jus-cookies-category-details">
                                    <div className="jus-cookies-category-detail">
                                        <span className="jus-cookies-detail-label">Purpose:</span>
                                        <span className="jus-cookies-detail-value">Personalization and user preferences</span>
                                    </div>
                                    <div className="jus-cookies-category-detail">
                                        <span className="jus-cookies-detail-label">Storage Duration:</span>
                                        <span className="jus-cookies-detail-value">Up to 12 months</span>
                                    </div>
                                    <div className="jus-cookies-category-detail">
                                        <span className="jus-cookies-detail-label">Examples:</span>
                                        <div className="jus-cookies-examples">
                                            <span>Language preference</span>
                                            <span>Region settings</span>
                                            <span>Accessibility options</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="jus-cookies-category-card">
                                <div className="jus-cookies-category-header">
                                    <div className="jus-cookies-category-icon" style={{ backgroundColor: "rgba(122, 162, 214, 0.1)" }}>
                                        <FaEye />
                                    </div>
                                    <h3>Security & Fraud Prevention</h3>
                                </div>
                                <p>Protect our platform and users from malicious activities, fraud, and unauthorized access attempts.</p>
                                <div className="jus-cookies-category-details">
                                    <div className="jus-cookies-category-detail">
                                        <span className="jus-cookies-detail-label">Purpose:</span>
                                        <span className="jus-cookies-detail-value">Security monitoring and threat detection</span>
                                    </div>
                                    <div className="jus-cookies-category-detail">
                                        <span className="jus-cookies-detail-label">Storage Duration:</span>
                                        <span className="jus-cookies-detail-value">Up to 6 months</span>
                                    </div>
                                    <div className="jus-cookies-category-detail">
                                        <span className="jus-cookies-detail-label">Examples:</span>
                                        <div className="jus-cookies-examples">
                                            <span>Login attempt tracking</span>
                                            <span>Bot detection</span>
                                            <span>Anomaly monitoring</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case "management":
                return (
                    <div className="jus-cookies-tab-content">
                        <div className="jus-cookies-content-header">
                            <div className="jus-cookies-content-icon" style={{ background: "linear-gradient(135deg, #b8945c 0%, #a57a42 100%)" }}>
                                <FaCog />
                            </div>
                            <div>
                                <h2>Managing Cookie Preferences</h2>
                                <p className="jus-cookies-content-subtitle">
                                    You have full control over your cookie preferences through browser settings and our consent management tools.
                                </p>
                            </div>
                        </div>

                        <div className="jus-cookies-management-flow">
                            <div className="jus-cookies-management-step">
                                <div className="jus-cookies-step-number">1</div>
                                <h3>Browser Settings</h3>
                                <p>Most browsers allow you to control cookies through their settings. You can usually find these in the "Options" or "Preferences" menu.</p>
                                <div className="jus-cookies-browser-guides">
                                    <span>Chrome: Settings → Privacy and security → Cookies</span>
                                    <span>Safari: Preferences → Privacy → Cookies</span>
                                    <span>Firefox: Options → Privacy & Security → Cookies</span>
                                    <span>Edge: Settings → Cookies and site permissions</span>
                                </div>
                            </div>

                            <div className="jus-cookies-management-step">
                                <div className="jus-cookies-step-number">2</div>
                                <h3>Cookie Consent Banner</h3>
                                <p>When you first visit Justivon, you'll see a cookie consent banner that allows you to accept, reject, or customize cookie categories.</p>
                                <ul>
                                    <li><strong>Granular controls</strong> for each cookie category</li>
                                    <li><strong>Easy access</strong> to change preferences anytime</li>
                                    <li><strong>Transparent information</strong> about each cookie type</li>
                                    <li><strong>Automatic preference saving</strong> for future visits</li>
                                </ul>
                            </div>

                            <div className="jus-cookies-management-step">
                                <div className="jus-cookies-step-number">3</div>
                                <h3>Mobile App Controls</h3>
                                <p>For mobile app users, cookie preferences can be managed through device settings and in-app privacy controls.</p>
                                <div className="jus-cookies-mobile-guides">
                                    <span><strong>iOS:</strong> Settings → Safari → Privacy & Security</span>
                                    <span><strong>Android:</strong> Chrome settings → Site settings → Cookies</span>
                                    <span><strong>In-app:</strong> Profile → Settings → Privacy</span>
                                </div>
                            </div>
                        </div>

                        <div className="jus-cookies-warning-box">
                            <FaExclamationTriangle className="jus-cookies-warning-icon" />
                            <div>
                                <h4>Important Notice</h4>
                                <p>
                                    Disabling strictly necessary cookies will prevent the platform from functioning correctly.
                                    Essential features like login, security protection, and form submissions require these cookies to operate.
                                </p>
                            </div>
                        </div>
                    </div>
                );

            case "third-party":
                return (
                    <div className="jus-cookies-tab-content">
                        <div className="jus-cookies-content-header">
                            <div className="jus-cookies-content-icon" style={{ background: "linear-gradient(135deg, #a57a42 0%, #8c6b35 100%)" }}>
                                <FaGlobe />
                            </div>
                            <div>
                                <h2>Third-Party Cookies & Services</h2>
                                <p className="jus-cookies-content-subtitle">
                                    Some cookies are set by trusted third-party services that help us provide a secure and feature-rich platform.
                                </p>
                            </div>
                        </div>

                        <div className="jus-cookies-provider-grid">
                            <div className="jus-cookies-provider-card">
                                <div className="jus-cookies-provider-header">
                                    <h3>Analytics Services</h3>
                                    <span className="jus-cookies-provider-badge">Optional</span>
                                </div>
                                <p>Third-party analytics services help us understand platform usage and improve user experience.</p>
                                <div className="jus-cookies-provider-details">
                                    <div className="jus-cookies-provider-detail">
                                        <span className="jus-cookies-detail-label">Provider:</span>
                                        <span className="jus-cookies-detail-value">Google Analytics</span>
                                    </div>
                                    <div className="jus-cookies-provider-detail">
                                        <span className="jus-cookies-detail-label">Purpose:</span>
                                        <span className="jus-cookies-detail-value">Usage analysis and improvement</span>
                                    </div>
                                    <div className="jus-cookies-provider-detail">
                                        <span className="jus-cookies-detail-label">Data Processing:</span>
                                        <span className="jus-cookies-detail-value">EU-based servers, GDPR compliant</span>
                                    </div>
                                </div>
                            </div>

                            <div className="jus-cookies-provider-card">
                                <div className="jus-cookies-provider-header">
                                    <h3>Security Services</h3>
                                    <span className="jus-cookies-provider-badge">Essential</span>
                                </div>
                                <p>Security providers help protect our platform from threats, fraud, and unauthorized access.</p>
                                <div className="jus-cookies-provider-details">
                                    <div className="jus-cookies-provider-detail">
                                        <span className="jus-cookies-detail-label">Provider:</span>
                                        <span className="jus-cookies-detail-value">Cloudflare</span>
                                    </div>
                                    <div className="jus-cookies-provider-detail">
                                        <span className="jus-cookies-detail-label">Purpose:</span>
                                        <span className="jus-cookies-detail-value">DDoS protection and security</span>
                                    </div>
                                    <div className="jus-cookies-provider-detail">
                                        <span className="jus-cookies-detail-label">Data Processing:</span>
                                        <span className="jus-cookies-detail-value">Global network, GDPR compliant</span>
                                    </div>
                                </div>
                            </div>

                            <div className="jus-cookies-provider-card">
                                <div className="jus-cookies-provider-header">
                                    <h3>Payment Processing</h3>
                                    <span className="jus-cookies-provider-badge">Essential</span>
                                </div>
                                <p>Payment processors enable secure transactions for legal services booked through our platform.</p>
                                <div className="jus-cookies-provider-details">
                                    <div className="jus-cookies-provider-detail">
                                        <span className="jus-cookies-detail-label">Provider:</span>
                                        <span className="jus-cookies-detail-value">Stripe</span>
                                    </div>
                                    <div className="jus-cookies-provider-detail">
                                        <span className="jus-cookies-detail-label">Purpose:</span>
                                        <span className="jus-cookies-detail-value">Secure payment processing</span>
                                    </div>
                                    <div className="jus-cookies-provider-detail">
                                        <span className="jus-cookies-detail-label">Data Processing:</span>
                                        <span className="jus-cookies-detail-value">PCI DSS compliant, GDPR aligned</span>
                                    </div>
                                </div>
                            </div>

                            <div className="jus-cookies-provider-card">
                                <div className="jus-cookies-provider-header">
                                    <h3>Communication Tools</h3>
                                    <span className="jus-cookies-provider-badge">Essential</span>
                                </div>
                                <p>Communication services enable secure messaging between users and legal professionals.</p>
                                <div className="jus-cookies-provider-details">
                                    <div className="jus-cookies-provider-detail">
                                        <span className="jus-cookies-detail-label">Provider:</span>
                                        <span className="jus-cookies-detail-value">SendGrid</span>
                                    </div>
                                    <div className="jus-cookies-provider-detail">
                                        <span className="jus-cookies-detail-label">Purpose:</span>
                                        <span className="jus-cookies-detail-value">Secure email communication</span>
                                    </div>
                                    <div className="jus-cookies-provider-detail">
                                        <span className="jus-cookies-detail-label">Data Processing:</span>
                                        <span className="jus-cookies-detail-value">EU-based, GDPR compliant</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case "consent":
                return (
                    <div className="jus-cookies-tab-content">
                        <div className="jus-cookies-content-header">
                            <div className="jus-cookies-content-icon" style={{ background: "linear-gradient(135deg, #d6b87c 0%, #c2a57a 100%)" }}>
                                <FaUserCheck />
                            </div>
                            <div>
                                <h2>Consent & Legal Framework</h2>
                                <p className="jus-cookies-content-subtitle">
                                    Our approach to cookie consent aligns with UK and EU data protection regulations, ensuring transparency and user control.
                                </p>
                            </div>
                        </div>

                        <div className="jus-cookies-consent-cards">
                            <div className="jus-cookies-consent-card" style={{ borderTop: "4px solid #d6b87c" }}>
                                <div className="jus-cookies-consent-number">
                                    <span>01</span>
                                </div>
                                <h3>Explicit Consent Requirement</h3>
                                <p>For non-essential cookies (analytics, marketing), we require explicit, informed consent before activation.</p>
                                <div className="jus-cookies-consent-features">
                                    <span>Clear opt-in mechanisms</span>
                                    <span>Granular category controls</span>
                                    <span>Easy withdrawal options</span>
                                </div>
                            </div>

                            <div className="jus-cookies-consent-card" style={{ borderTop: "4px solid #c2a57a" }}>
                                <div className="jus-cookies-consent-number">
                                    <span>02</span>
                                </div>
                                <h3>Legitimate Interest Basis</h3>
                                <p>Essential cookies operate under legitimate interest to provide secure, functional services you request.</p>
                                <div className="jus-cookies-consent-features">
                                    <span>Security protection</span>
                                    <span>Service delivery</span>
                                    <span>Fraud prevention</span>
                                </div>
                            </div>

                            <div className="jus-cookies-consent-card" style={{ borderTop: "4px solid #b8945c" }}>
                                <div className="jus-cookies-consent-number">
                                    <span>03</span>
                                </div>
                                <h3>Withdrawal of Consent</h3>
                                <p>You can withdraw cookie consent at any time through our preference center or browser settings.</p>
                                <div className="jus-cookies-consent-features">
                                    <span>Immediate effect</span>
                                    <span>Preference persistence</span>
                                    <span>No service degradation for essentials</span>
                                </div>
                            </div>

                            <div className="jus-cookies-consent-card" style={{ borderTop: "4px solid #a57a42" }}>
                                <div className="jus-cookies-consent-number">
                                    <span>04</span>
                                </div>
                                <h3>Cookie Lifespan & Retention</h3>
                                <p>Cookies have specific lifespans aligned with their purpose, with regular review and expiration.</p>
                                <div className="jus-cookies-consent-features">
                                    <span>Session cookies</span>
                                    <span>Persistent cookies</span>
                                    <span>Automatic expiration</span>
                                </div>
                            </div>
                        </div>

                        <div className="jus-cookies-legal-notice">
                            <FaShieldAlt className="jus-cookies-legal-icon" />
                            <div>
                                <h4>Legal Compliance Framework</h4>
                                <p>
                                    Our cookie practices comply with UK GDPR, Data Protection Act 2018, PECR regulations, and ICO guidance.
                                    We conduct regular audits to ensure ongoing compliance with evolving legal requirements.
                                </p>
                            </div>
                        </div>
                    </div>
                );

            case "updates":
                return (
                    <div className="jus-cookies-tab-content">
                        <div className="jus-cookies-content-header">
                            <div className="jus-cookies-content-icon" style={{ background: "linear-gradient(135deg, #c2a57a 0%, #b8945c 100%)" }}>
                                <FaHistory />
                            </div>
                            <div>
                                <h2>Policy Updates & Contact</h2>
                                <p className="jus-cookies-content-subtitle">
                                    We regularly review and update our Cookies Policy to reflect changes in technology, regulations, and our services.
                                </p>
                            </div>
                        </div>

                        <div className="jus-cookies-update-timeline">
                            <div className="jus-cookies-timeline-item">
                                <div className="jus-cookies-timeline-dot"></div>
                                <div className="jus-cookies-timeline-content">
                                    <h3>Regular Review Cycle</h3>
                                    <p>This Cookies Policy undergoes bi-annual review, with additional reviews triggered by regulatory changes or technological updates.</p>
                                </div>
                            </div>

                            <div className="jus-cookies-timeline-item">
                                <div className="jus-cookies-timeline-dot"></div>
                                <div className="jus-cookies-timeline-content">
                                    <h3>Change Notification</h3>
                                    <p>Material changes to cookie practices are communicated through platform notifications and updated consent banners.</p>
                                </div>
                            </div>

                            <div className="jus-cookies-timeline-item">
                                <div className="jus-cookies-timeline-dot"></div>
                                <div className="jus-cookies-timeline-content">
                                    <h3>Version Archiving</h3>
                                    <p>Previous versions of this policy are maintained for transparency and can be provided upon request.</p>
                                </div>
                            </div>
                        </div>

                        <div className="jus-cookies-contact-section">
                            <div className="jus-cookies-contact-card">
                                <div className="jus-cookies-contact-header">
                                    <FaEnvelope className="jus-cookies-contact-main-icon" />
                                    <h3>Contact & Inquiries</h3>
                                </div>
                                <div className="jus-cookies-contact-details">
                                    <div className="jus-cookies-contact-item">
                                        <strong>Privacy Questions</strong>
                                        <p>{contactEmail || "privacy@justivon.com"}</p>
                                    </div>
                                    <div className="jus-cookies-contact-item">
                                        <strong>Response Time</strong>
                                        <p>Within 5 business days for cookie-related inquiries</p>
                                    </div>
                                    <div className="jus-cookies-contact-item">
                                        <strong>Technical Support</strong>
                                        <p>For cookie management assistance</p>
                                    </div>
                                </div>
                            </div>

                            <div className="jus-cookies-regulation-card">
                                <div className="jus-cookies-regulation-header">
                                    <FaShieldAlt className="jus-cookies-regulation-icon" />
                                    <h3>Regulatory Information</h3>
                                </div>
                                <p>
                                    For concerns about our cookie practices, you may contact the Information Commissioner's Office (ICO),
                                    the UK's independent authority for data protection.
                                </p>
                                <a
                                    href="https://www.ico.org.uk"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="jus-cookies-ico-link"
                                >
                                    Visit ICO Website <FaChevronRight />
                                </a>
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="jus-cookies-container">
            <Header />

            {/* Hero Section */}
            <section className="jus-cookies-hero">
                <div className="jus-cookies-hero-content">
                    <div className="jus-cookies-hero-badge">
                        <FaCookieBite />
                        <span>Cookie Transparency</span>
                    </div>
                    <h1 className="jus-cookies-hero-title">
                        Cookies <span className="jus-cookies-highlight">Policy</span>
                    </h1>
                    <p className="jus-cookies-hero-subtitle">
                        This Cookies Policy explains how Justivon uses cookies and similar
                        technologies to ensure a secure, reliable, and optimized experience.
                    </p>
                    <div className="jus-cookies-hero-meta">
                        <div className="jus-cookies-meta-item">
                            <FaClock />
                            <span>Last Updated: March 2024</span>
                        </div>
                        <div className="jus-cookies-meta-item">
                            <FaShieldAlt />
                            <span>GDPR & UK DPA Compliant</span>
                        </div>
                        <div className="jus-cookies-meta-item">
                            <FaUserCheck />
                            <span>Granular Consent Controls</span>
                        </div>
                    </div>
                </div>
                <div className="jus-cookies-hero-pattern"></div>
            </section>

            {/* Main Content */}
            <main className="jus-cookies-main">
                <div className="jus-cookies-content-wrapper">
                    {/* Navigation Sidebar */}
                    <nav className="jus-cookies-sidebar">
                        <div className="jus-cookies-sidebar-header">
                            <h3>Cookies Policy Sections</h3>
                            <p>Navigate through our comprehensive cookie framework</p>
                        </div>
                        <div className="jus-cookies-sidebar-tabs">
                            {tabs.map(tab => (
                                <button
                                    key={tab.id}
                                    className={`jus-cookies-sidebar-tab ${activeTab === tab.id ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    <div
                                        className="jus-cookies-tab-indicator"
                                        style={{ backgroundColor: tab.color }}
                                    ></div>
                                    <div className="jus-cookies-tab-content-wrapper">
                                        <div className="jus-cookies-tab-icon" style={{ color: tab.color }}>
                                            {tab.icon}
                                        </div>
                                        <div className="jus-cookies-tab-text">
                                            <span className="jus-cookies-tab-title">{tab.title}</span>
                                            <span className="jus-cookies-tab-subtitle">{tab.subtitle}</span>
                                        </div>
                                        {activeTab === tab.id && (
                                            <FaChevronRight className="jus-cookies-tab-arrow" />
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </nav>

                    {/* Content Area */}
                    <div className="jus-cookies-content-container">
                        <div className="jus-cookies-content-area">
                            {renderContent()}
                        </div>
                    </div>
                </div>

                {/* Resources Section */}
                <div className="jus-cookies-resources">
                    <div className="jus-cookies-resources-header">
                        <h3>Cookie Resources</h3>
                        <p>Additional tools and information for cookie management</p>
                    </div>
                    <div className="jus-cookies-resources-grid">
                        <button
                            className="jus-cookies-resource-card"
                            onClick={() => {
                                setActiveTab("management");
                                window.scrollTo({
                                    top: 490, // adjust if header height changes
                                    behavior: "smooth"
                                });
                            }}
                        >
                            <div className="jus-cookies-resource-icon">
                                <FaCog />
                            </div>
                            <div className="jus-cookies-resource-content">
                                <h4>Manage Cookies</h4>
                                <p>Update your cookie preferences</p>
                            </div>
                            <FaChevronRight className="jus-cookies-resource-arrow" />
                        </button>

                        <a href="/faq" className="jus-cookies-resource-card">
                            <div className="jus-cookies-resource-icon">
                                <FaQuestionCircle />
                            </div>
                            <div className="jus-cookies-resource-content">
                                <h4>Cookie FAQ</h4>
                                <p>Common questions answered</p>
                            </div>
                            <FaChevronRight className="jus-cookies-resource-arrow" />
                        </a>

                        <a href="#download" className="jus-cookies-resource-card">
                            <div className="jus-cookies-resource-icon">
                                <FaDownload />
                            </div>
                            <div className="jus-cookies-resource-content">
                                <h4>Download Policy</h4>
                                <p>PDF version for records</p>
                            </div>
                            <FaChevronRight className="jus-cookies-resource-arrow" />
                        </a>

                        <a
                            href={`mailto:${contactEmail || "privacy@justivon.com"}`}
                            className="jus-cookies-resource-card"
                        >
                            <div className="jus-cookies-resource-icon">
                                <FaEnvelope />
                            </div>
                            <div className="jus-cookies-resource-content">
                                <h4>Contact Privacy</h4>
                                <p>Questions about cookies</p>
                            </div>
                            <FaChevronRight className="jus-cookies-resource-arrow" />
                        </a>
                    </div>
                </div>
            </main>
            <ChatBot />
            <Footer />
        </div>
    );
}